// The typed fetch wrapper every apps/web API call goes through. Parses every response
// with a shared Zod schema (app-engineering §2: parse, don't validate) and resolves to
// a Result<T, ClientError> - never a thrown exception, never a hand-rolled shape the
// caller re-validates. Structured refusals (app-limit, intent-unset) are narrowed here
// once so screens branch on `error.kind`, never re-parse a response body themselves.
import type { z } from "zod";
import { err, ok } from "@nightcrew/shared";
import type { Result } from "@nightcrew/shared";
import { AppLimitRefusal, type ClientError } from "./errors.js";

const JSON_HEADERS = { "Content-Type": "application/json" } as const;

/** A loose parse of `{code, message}` - just enough to route to the right ClientError branch. */
const looksLikeAppError = (value: unknown): value is { code: string; message: string } =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as Record<string, unknown>)["code"] === "string" &&
  typeof (value as Record<string, unknown>)["message"] === "string";

const readJson = async (response: Response): Promise<unknown> => {
  const text = await response.text();
  if (text.length === 0) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
};

const toClientErrorFromErrorBody = (body: unknown): ClientError => {
  if (!looksLikeAppError(body)) {
    return { kind: "malformed_response", message: "the server returned an error with no body" };
  }
  if (body.code === "app_limit_reached") {
    const parsed = AppLimitRefusal.safeParse(body);
    if (parsed.success) return { kind: "app_limit_refusal", refusal: parsed.data };
    return {
      kind: "malformed_response",
      message: "app-limit refusal body did not match the expected shape",
    };
  }
  if (body.code === "intent_required" || body.code === "fingerprint_required") {
    return { kind: "route_not_ready", error: { code: body.code, message: body.message } };
  }
  return { kind: "boundary", error: { code: body.code, message: body.message } };
};

/**
 * Fetch `path`, parse the response body with `schema` on success, and narrow any
 * failure (network, non-2xx, or a 2xx body that fails the schema) into a `ClientError`.
 * Always sends the session cookie (`credentials: "include"`) - Better Auth's cookie is
 * the only auth carrier past sign-in (contracts.md "Onboarding/app API surface").
 */
export const apiFetch = async <T>(
  path: string,
  schema: z.ZodType<T>,
  init: RequestInit = {},
): Promise<Result<T, ClientError>> => {
  // `HeadersInit` is a union that includes `string[][]`, so spreading `init.headers`
  // straight into an object literal risks producing numeric-index keys instead of
  // header names (no-misused-spread) - build a real Headers instance instead, which
  // normalizes every HeadersInit variant correctly.
  //
  // Certify's certificate upload (src/api/certify.ts) is this client's first multipart
  // caller (the platform's first sensitive-upload path, ADR-0020 decision 3/4): a
  // `FormData` body must NOT carry a hardcoded `application/json` content type - the
  // browser sets `multipart/form-data; boundary=...` itself, and stamping JSON over it
  // breaks the multipart parse server-side. Every existing JSON caller is unaffected
  // (their `init.body` is never a `FormData` instance).
  const headers = new Headers(init.body instanceof FormData ? {} : JSON_HEADERS);
  if (init.headers !== undefined) {
    for (const [key, value] of new Headers(init.headers)) headers.set(key, value);
  }

  let response: Response;
  try {
    response = await fetch(path, { ...init, credentials: "include", headers });
  } catch (cause: unknown) {
    const message = cause instanceof Error ? cause.message : "network request failed";
    return err({ kind: "network", message });
  }

  const body = await readJson(response);

  if (!response.ok) {
    return err(toClientErrorFromErrorBody(body));
  }

  const parsed = schema.safeParse(body);
  if (!parsed.success) {
    return err({
      kind: "malformed_response",
      message: `response for ${path} failed schema validation: ${parsed.error.issues.map((i) => i.path.join(".")).join(", ")}`,
    });
  }
  return ok(parsed.data);
};

/** POST helper: JSON-encodes `body` and calls {@link apiFetch}. */
export const apiPost = async <T>(
  path: string,
  schema: z.ZodType<T>,
  body: unknown,
): Promise<Result<T, ClientError>> =>
  apiFetch(path, schema, { method: "POST", body: JSON.stringify(body) });
